import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { CHATBOT_NAME_LIMIT } from 'shared/constants/limits'
import { uiStore } from 'shared/store/uiStore'
import { toastStore } from 'shared/ui'
import { logger } from 'shared/lib'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ChatbotApi, ChatbotInstructionType, UnifiedAgent } from 'entities/Chatbot'
import { sendEnhancePromptLogEvent } from 'entities/AIAgent/actions/enhancePrompt/sendEnhancePromptLogEvent'
import { ChatbotRoutes } from 'entities/ExportRouters'
import { NameInputStore } from 'features/CreateOrEdit/NameInput'
import { TriggerSectionStore } from 'pages/chatbot/ui/UnifiedAgent/ui/TriggerSection/store/triggerSectionStore'
import { InstructionsSectionStore } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/store/instructionsSectionStore'
import { BookingSectionStore } from 'pages/chatbot/ui/UnifiedAgent/ui/BookingSection/store/bookingSectionStore'
import { AutoFollowUpSectionStore } from 'pages/chatbot/ui/UnifiedAgent/ui/AutoFollowUpSection/store/autoFollowUpSectionStore'
import { KnowledgeBaseSectionStore } from 'pages/chatbot/ui/UnifiedAgent/ui/KnowledgeBaseSection/store/knowledgeBaseSectionStore'
import { chatbotOperation } from 'pages/chatbot/store/chatbotOperationStore'

export type IAgentConfigureTab = typeof ChatbotRoutes.chat | typeof ChatbotRoutes.config

export class UnifiedAgentStore {
  id: number | null
  readonly model: UnifiedAgent
  readonly nameInputStore: NameInputStore
  readonly triggerSectionStore: TriggerSectionStore
  readonly instructionsSectionStore: InstructionsSectionStore
  readonly bookingSectionStore: BookingSectionStore
  readonly autoFollowUpSectionStore: AutoFollowUpSectionStore
  readonly knowledgeBaseSectionStore: KnowledgeBaseSectionStore
  isSaving = false
  activeConfigureTab: IAgentConfigureTab = ChatbotRoutes.chat
  isLoadingSection = false

  constructor(agentId: number | null, readonly entities: IEntitiesStore) {
    this.id = agentId
    this.model = new UnifiedAgent()
    this.nameInputStore = new NameInputStore({
      name: '',
      limit: CHATBOT_NAME_LIMIT,
      isResetErrorOnChange: true,
    })
    this.triggerSectionStore = new TriggerSectionStore(entities, this.model.trigger)
    this.bookingSectionStore = new BookingSectionStore(entities, this.model.booking)
    this.autoFollowUpSectionStore = new AutoFollowUpSectionStore(this.model.autoFollowUp)
    this.knowledgeBaseSectionStore = new KnowledgeBaseSectionStore(this.model.knowledgeBase)
    this.instructionsSectionStore = new InstructionsSectionStore(
      entities,
      agentId,
      this.model.instructions,
      undefined,
      this.requestUnifiedEnhance
    )
    makeAutoObservable(this)
  }

  get isConfigureTab() {
    return this.activeConfigureTab === ChatbotRoutes.config
  }

  get commonEnhancePayload() {
    return {
      chatbotId: this.id ?? 0,
      instruction_fields: this.instructionsSectionStore.existMergeFieldsPayload,
      booking_fields: this.bookingSectionStore.bookingFields,
      settings: {
        chatbot_type: ChatbotInstructionType.Unified,
        instructions: this.instructionsSectionStore.instructions,
        book_appointment_enabled: this.bookingSectionStore.bookingEnabled,
        book_appointment_type: this.bookingSectionStore.bookingType,
        enable_followup: this.autoFollowUpSectionStore.enabled,
        followup_assertiveness: this.autoFollowUpSectionStore.assertiveness,
        followup_window: this.autoFollowUpSectionStore.followUpWindow,
        use_knowledge_base: this.knowledgeBaseSectionStore.useKnowledgeBase,
        use_live_documents: this.knowledgeBaseSectionStore.useLiveDocuments,
      },
    }
  }

  get payload() {
    return {
      ...this.model.toJSON(),
      name: this.nameInputStore.name,
      send_from: this.triggerSectionStore.sendFrom,
      filters: this.triggerSectionStore.paramsFilters,
      knowledge_base_documents: this.knowledgeBaseSectionStore.documents,
      fields: this.instructionsSectionStore.existMergeFieldsPayload,
    }
  }

  requestUnifiedEnhance = async (instruction: string, signal?: AbortSignal): Promise<string> => {
    sendEnhancePromptLogEvent(this.entities, {
      agentType: 'Unified Agent',
      agent_id: this.id ?? 0,
    })
    const { data } = await ChatbotApi.enhanceUnifiedPrompt(
      {
        ...this.commonEnhancePayload,
        settings: { ...this.commonEnhancePayload.settings, instructions: instruction },
        instructions: 'Enhance the prompt',
      },
      { signal }
    )
    return data.improvedPrompt
  }

  setActiveConfigureTab = (tab: IAgentConfigureTab) => {
    if (tab !== this.activeConfigureTab) {
      this.bookingSectionStore.resetSection()
      this.knowledgeBaseSectionStore.resetSection()
    }
    this.activeConfigureTab = tab
  }

  setIsSaving = (isSaving: boolean) => {
    this.isSaving = isSaving
  }

  titleValidate = () => {
    if (!this.nameInputStore.name) {
      this.nameInputStore.setError('Please add a name for your AI Agent.')
      return false
    }
    return true
  }

  saveValidate = () => {
    const titleValid = this.titleValidate()
    const enrollmentValid = this.triggerSectionStore.validateEnrollment()
    return titleValid && enrollmentValid
  }

  handleSaveAgent = async () => {
    this.setIsSaving(true)

    if (this.id) {
    } else {
      this.createAgent().then((id) => {
        this.setIsSaving(false)
        if (id) uiStore.changeRoute({ path: `/${ChatbotRoutes.root}/${id}` })
      })
    }
  }

  createAgent: (confirmTest?: boolean) => Promise<number | null> = async (confirmTest) => {
    if (!this.titleValidate()) return null

    if (confirmTest) {
      const confirmed = await chatbotOperation.confirmTest(
        this.nameInputStore.name,
        this.model.isActive
      )

      if (!confirmed) return null
    }

    try {
      const { data } = await chatbotOperation.create(this.payload)

      toastStore.add({
        title: 'AI Agent saved',
        type: 'info',
      })

      return runInAction(() => {
        this.id = data.id

        return data.id
      })
    } catch (error) {
      if (error instanceof AxiosError) this._syncErrors(error.response?.data?.errors ?? {})

      logger.error(error)
      toastStore.add({
        title: 'Something went wrong',
        desc: 'Please try again',
        type: 'error',
      })

      return null
    }
  }

  private _syncErrors = (errors: Record<string, string[]>) => {
    const sendFromError = errors['send_from']?.[0] ?? errors['send_from.options']?.[0]
    const instructionsError = errors['settings.instructions.value']?.[0]
    const bookingLinkError = errors['settings.book_appointment_link.value']?.[0]

    if (sendFromError) this.triggerSectionStore.setSendFromError(sendFromError)
    if (instructionsError) this.instructionsSectionStore.setError(instructionsError)
    if (bookingLinkError) this.bookingSectionStore.handleBookingLinkError(bookingLinkError)
  }

  createAndPublishAgent: (confirmTest?: boolean) => Promise<number | null> = async () => {
    if (!this.saveValidate()) return null
    if (this.model.knowledgeBase.useKnowledgeBase) {
      const validateAgentKnowledgeBaseStore =
        this.knowledgeBaseSectionStore.agentKnowledgeBaseStore.checkValidate()
      if (!validateAgentKnowledgeBaseStore) return null
    }

    try {
      const { data } = await chatbotOperation.create({ ...this.payload, is_active: true })

      // if (data.is_active) await chatbotOperation.enroll(data.id, data.name, data.filters)

      return runInAction(() => {
        this.id = data.id

        return data.id
      })
    } catch (error) {
      if (error instanceof AxiosError) this._syncErrors(error.response?.data?.errors ?? {})

      logger.error(error)
      toastStore.add({
        title: 'Failed to activate',
        desc: 'Complete all required fields',
        type: 'error',
      })

      return null
    }
  }

  handlePublishAgent = async () => {
    this.setIsSaving(true)

    const isPublished = this.id ? () => {} : await this.createAndPublishAgent()
    this.setIsSaving(false)

    if (isPublished)
      uiStore.changeRoute({ path: `/${ChatbotRoutes.root}/${ChatbotRoutes.texting}` })
  }

  updateInstructions = async (section: string) => {
    let instructions = ''
    let sectionSync = null

    if (section === 'knowledgeBase') {
      if (!this.knowledgeBaseSectionStore.validateSection()) return
      instructions = this.knowledgeBaseSectionStore.sectionInstruction
      sectionSync = this.knowledgeBaseSectionStore.init
    } else if (section === 'booking') {
      if (!this.bookingSectionStore.validateSection()) return
      instructions = this.bookingSectionStore.sectionInstruction
      sectionSync = this.bookingSectionStore.init
    }

    if (!sectionSync) return

    this.isLoadingSection = true
    try {
      const { data } = await ChatbotApi.enhanceUnifiedPrompt({
        ...this.commonEnhancePayload,
        settings: {
          ...this.commonEnhancePayload.settings,
          ...this.bookingSectionStore.enhancePayload(section === 'booking'),
          ...this.knowledgeBaseSectionStore.enhancePayload(section === 'knowledgeBase'),
        },
        instructions,
      })
      runInAction(() => {
        this.instructionsSectionStore.setInstructions(data.improvedPrompt)
        sectionSync()
      })
    } catch (error) {
      toastStore.add({
        title: 'Something went wrong',
        desc: 'Please try again',
        type: 'error',
      })
      logger.error(error)
    } finally {
      runInAction(() => {
        this.isLoadingSection = false
      })
    }
  }

  resetEditSection = (section: string) => {
    switch (section) {
      case 'knowledgeBase':
        this.knowledgeBaseSectionStore.resetSection()
        break
      case 'booking':
        this.bookingSectionStore.resetSection()
        break
      default:
        break
    }
  }

  dispose = () => {
    this.bookingSectionStore.dispose()
    this.instructionsSectionStore.dispose()
  }
}
